// GG Anvil Sparks — logo shard burst on tap/click.
// Brand-matched to the GG Anvil logo: white + orange kite shards with a
// small ring pulse on every strike.
//
// Adapted from the standalone drop-in script (gg-anvil-sparks.js) so it has
// a start/stop lifecycle instead of running forever from an IIFE. The colors
// and core shard silhouette match the source script; three behaviours diverge
// on purpose (2026-07-06, Lisa's call): the burst fires only every 3rd click
// (BURST_EVERY) instead of every click, each burst gets a random overall
// scale so strikes vary in size, and native multi-click text selection is
// cancelled so rapid clicking no longer leaves a stray highlight.
//
// startSparks() returns a teardown function that removes the pointerdown/
// resize listeners, cancels any in-flight rAF loop, and removes the canvas
// from the DOM. Call the teardown on unmount (see
// components/AmbientEffects.tsx) — without it, navigating between public
// pages would stack a second canvas + a second pointerdown listener on top
// of the first.

// Optional per-burst overrides — see burst() below. Exposed so callers of
// sparkBurstAt() (Phase 4b's edge-spark flourish) can ask for a smaller,
// ring-less burst than a real click gets, instead of re-deriving the shard
// physics themselves.
type BurstOptions = { scale?: number; shardCount?: number; ring?: boolean };

// The single running burst() closure, captured once startSparks() mounts its
// canvas (see AmbientEffects.tsx — exactly one instance runs for the whole
// app session). null whenever no spark canvas is currently mounted.
let activeBurst: ((x: number, y: number, opts?: BurstOptions) => void) | null = null;

/**
 * Fire a spark burst at an arbitrary page point, reusing the SAME running
 * particle system startSparks() already mounted — no new canvas, no new
 * particle math. No-ops if the spark canvas isn't currently mounted, or
 * under prefers-reduced-motion.
 *
 * Added for the Setup checklist's edge-spark flourish (Phase 4b) — lets a
 * caller (e.g. TournamentSetup's onHighlight hook) trigger the existing
 * brand-matched shard burst at a computed point without needing a real
 * pointerdown event.
 */
export function sparkBurstAt(x: number, y: number, opts?: BurstOptions): void {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  activeBurst?.(x, y, opts);
}

export function startSparks(): () => void {
  /* ============ TUNING KNOBS ============ */
  const ONLY_ON_SELECTOR: string | null = null; // null = whole page. Or e.g. 'button, a, .spark'
  const SHARD_COUNT = 14; // shards per strike
  const SPEED_MIN = 2; // launch speed range
  const SPEED_MAX = 8;
  const SHARD_SIZE_MAX = 14; // max shard length in px
  const GRAVITY = 0.1; // slight downward pull
  const DRAG = 0.96; // air resistance
  const RING_PULSE = true; // small expanding ring on impact
  const COLORS = [
    // GG Anvil brand palette
    '#FFFFFF', // white (logo mark)
    '#F5821F', // primary orange
    '#F79B57', // mid orange
    '#FBB584', // light orange
  ];
  const RING_COLOR = '#F79B57';
  /* ====================================== */

  // Full-screen canvas that never blocks touches/clicks
  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText =
    'position:fixed;inset:0;width:100vw;height:100vh;' + 'pointer-events:none;z-index:2147483647;';
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d')!;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  type Shard = {
    t: 'shard';
    x: number;
    y: number;
    vx: number;
    vy: number;
    spin: number;
    len: number;
    wid: number;
    life: number;
    decay: number;
    c: string;
  };
  type Ring = {
    t: 'ring';
    x: number;
    y: number;
    r: number;
    vr: number;
    life: number;
    decay: number;
    w: number;
    c: string;
  };
  type Particle = Shard | Ring;

  let parts: Particle[] = [];
  let running = false;
  let rafId: number | null = null;
  let clickCount = 0; // gates the burst to every Nth strike (see BURST_EVERY)
  const BURST_EVERY = 3; // only spark on every 3rd click — calmer on rapid clicking
  const reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function resize() {
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  resize();
  window.addEventListener('resize', resize);

  function rand(min: number, max: number) {
    return min + Math.random() * (max - min);
  }

  function pick<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  // Draws an elongated kite shape (same silhouette as the logo shards),
  // pointing along `ang`, with a short tail behind the pivot.
  function drawShard(x: number, y: number, ang: number, len: number, wid: number, color: string, alpha: number) {
    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    const ca = Math.cos(ang),
      sa = Math.sin(ang);
    ctx.beginPath();
    ctx.moveTo(x + ca * len, y + sa * len); // nose
    ctx.lineTo(x - sa * wid, y + ca * wid); // side
    ctx.lineTo(x - ca * len * 0.45, y - sa * len * 0.45); // tail
    ctx.lineTo(x + sa * wid, y - ca * wid); // side
    ctx.closePath();
    ctx.fill();
  }

  function burst(x: number, y: number, opts?: BurstOptions) {
    // Each burst gets a random overall scale so consecutive strikes read
    // noticeably bigger or smaller — spread, shard length/width and the ring
    // all ride the same factor so the whole burst stays coherent. A caller
    // (sparkBurstAt) can pin the scale/shard count/ring instead — the edge
    // flourish uses this to stay a restrained trace, not a full click-strike.
    const scale = opts?.scale ?? rand(0.6, 1.7);
    const shardCount = opts?.shardCount ?? SHARD_COUNT;
    const ring = opts?.ring ?? RING_PULSE;
    for (let i = 0; i < shardCount; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rand(SPEED_MIN, SPEED_MAX) * scale;
      parts.push({
        t: 'shard',
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        spin: rand(-0.15, 0.15),
        len: rand(SHARD_SIZE_MAX * 0.4, SHARD_SIZE_MAX) * scale,
        wid: rand(1.5, 3.5) * scale,
        life: 1,
        decay: rand(0.02, 0.045),
        c: pick(COLORS),
      });
    }
    if (ring) {
      parts.push({
        t: 'ring',
        x,
        y,
        r: 2 * scale,
        vr: 5 * scale,
        life: 1,
        decay: 0.09,
        w: 1.5,
        c: RING_COLOR,
      });
    }
    if (!running) {
      running = true;
      rafId = requestAnimationFrame(tick);
    }
  }

  function tick() {
    // CSS-px clear (dpr-safe): passing canvas.width/height double-scales under setTransform(dpr)
    // and under-clears when dpr < 1 (zoom below 100%), leaving a smeared band. See emberEffect.ts.
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i];
      p.life -= p.decay;
      if (p.life <= 0) {
        parts.splice(i, 1);
        continue;
      }
      if (p.t === 'shard') {
        p.vy += GRAVITY;
        p.vx *= DRAG;
        p.vy *= DRAG;
        p.x += p.vx;
        p.y += p.vy;
        const ang = Math.atan2(p.vy, p.vx) + p.spin * (1 - p.life) * 10;
        drawShard(p.x, p.y, ang, p.len * p.life, p.wid * Math.max(p.life, 0.4), p.c, Math.max(p.life, 0));
      } else if (p.t === 'ring') {
        p.r += p.vr;
        p.vr *= 0.92;
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.strokeStyle = p.c;
        ctx.lineWidth = p.w;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
    if (parts.length) {
      rafId = requestAnimationFrame(tick);
    } else {
      running = false;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight); // CSS-px, dpr-safe (see tick())
    }
  }

  function onPointerDown(e: PointerEvent) {
    if (reducedMotion) return; // respect accessibility setting
    if (ONLY_ON_SELECTOR) {
      const target = e.target as HTMLElement | null;
      if (!(target && target.closest && target.closest(ONLY_ON_SELECTOR))) {
        return;
      }
    }
    // Only burst on every Nth strike so rapid clicking stays calm.
    clickCount++;
    if (clickCount % BURST_EVERY !== 0) return;
    burst(e.clientX, e.clientY);
  }
  document.addEventListener('pointerdown', onPointerDown, { passive: true });

  // Rapid clicking on the same spot makes the browser text-select (double =
  // word, triple = line), leaving a stray highlight behind the sparks. Cancel
  // that native multi-click selection (e.detail > 1) EXCEPT inside real form
  // fields, where double-click-to-select-a-word is expected behaviour.
  const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
  function onMouseDownDeselect(e: MouseEvent) {
    if (e.detail <= 1) return; // let single clicks and drag-selection through
    const target = e.target as HTMLElement | null;
    if (target && target.closest && target.closest(EDITABLE)) return;
    e.preventDefault();
  }
  document.addEventListener('mousedown', onMouseDownDeselect);

  // Publish this instance's burst() for sparkBurstAt() to call — see the
  // type/comment above startSparks().
  activeBurst = burst;

  return function stopSparks() {
    running = false;
    if (rafId !== null) cancelAnimationFrame(rafId);
    window.removeEventListener('resize', resize);
    document.removeEventListener('pointerdown', onPointerDown);
    document.removeEventListener('mousedown', onMouseDownDeselect);
    canvas.remove();
    parts = [];
    if (activeBurst === burst) activeBurst = null;
  };
}
